import { PayloadAction } from '@reduxjs/toolkit';

import { Position } from 'app/components/GoogleMaps/interfaces';
import { UserType } from 'app/domain/authentication/constants';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { StringOrNull } from 'utils/types/types';

import {
  AgentNotFoundErrorData,
  ChangePhoneNumberErrorTypes,
  DomicileOutOfCoverageErrorType,
  GeoplaceListErrorType,
  LeadExpiredErrorData,
  MaxAgeApplicantKTPErrorType,
  MaximumAttemptEditKTPErrorType,
  OTPErrorTypes,
  PersonalInformationState,
  PhoneNumberErrorType,
  ProfileImage,
  SurveyorSubmittedErrorType,
  TooManyAttemptErrorType,
  TouchPointActionType,
  UpdateProfileState,
  VerificationProfileState,
  WrongOTPErrorType,
} from './interfaces';

export interface ProfileState {
  agentId: StringOrNull;
  bravoLeadId: StringOrNull;
  groupId: StringOrNull;
  applicationNumber: StringOrNull;
  applicationId: StringOrNull;
  isDigitalApplication: boolean;
  profileData: VerificationProfileState;
  personalInformationData: PersonalInformationState | null;
  latLngData: Position;
  changePhoneNumberError: ChangePhoneNumberErrorTypes | '';
  otpError: OTPErrorTypes | '';
  phoneNumberNotMatchError: PhoneNumberErrorType | '';
  tooManyAttemptError: TooManyAttemptErrorType | '';
  tooManyAttemptResendError: TooManyAttemptErrorType | '';
  wrongOTPError: WrongOTPErrorType | '';
  touchPointAction: TouchPointActionType | '';
  domicileOutOfCoverageError: DomicileOutOfCoverageErrorType | '';
  maximumAttemptEditKTPError: MaximumAttemptEditKTPErrorType | '';
  maxAgeApplicantKTPError: MaxAgeApplicantKTPErrorType | '';
  surveyorSubmittedError: SurveyorSubmittedErrorType | '';
  userTypeSubmitted: UserType.CAS | UserType.SURVEYOR | '';
  geoplaceError: GeoplaceListErrorType | '';
  isOpenGeoplaceScreen: boolean;
  agentNotFoundError: AgentNotFoundErrorData;
  leadExpiredError: LeadExpiredErrorData | null;
  isBlackListArea: boolean;
}

export const initialState: ProfileState = {
  applicationId: null,
  isDigitalApplication: false,
  bravoLeadId: null,
  agentId: null,
  groupId: null,
  applicationNumber: null,
  profileData: {
    dukcapilCheckNeeded: false,
    selfie_image: '',
    liveliness_scores: null,
    id_card_similarity: null,
    ktp: '',
    id_number: '',
    birth_place: '',
    birth_date: '',
    nationality: '',
    rtrw: '',
    occupation: '',
    gender: '',
    full_name: '',
    marriage_type: '',
    ktp_temp: '',
    email: '',
    address: {
      province_code: '',
      province_name: '',
      city_code: '',
      city_name: '',
      district_code: '',
      district_name: '',
      sub_district_code: '',
      sub_district_name: '',
      zip_code: '',
      address_detail: '',
    },
    religion: null,
  },
  personalInformationData: null,
  touchPointAction: '',
  latLngData: { lat: 0, lng: 0 },
  changePhoneNumberError: '',
  otpError: '',
  phoneNumberNotMatchError: '',
  tooManyAttemptError: '',
  tooManyAttemptResendError: '',
  wrongOTPError: '',
  domicileOutOfCoverageError: '',
  maximumAttemptEditKTPError: '',
  maxAgeApplicantKTPError: '',
  surveyorSubmittedError: '',
  userTypeSubmitted: '',
  geoplaceError: '',
  agentNotFoundError: {
    code: '',
    data: {
      applicationId: '',
      marketingId: '',
      branchCode: '',
      errorCode: '',
    },
  },
  isOpenGeoplaceScreen: false,
  leadExpiredError: null,
  isBlackListArea: false,
};

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    addProfileData(state, action: PayloadAction<VerificationProfileState>) {
      state.profileData = action.payload;
    },
    updateProfileData(state, action: PayloadAction<UpdateProfileState>) {
      state.profileData = { ...state.profileData, ...action.payload };
    },
    updateAddress(
      state,
      action: PayloadAction<VerificationProfileState['address']>,
    ) {
      state.profileData.address = action.payload;
    },
    addPersonalInformationData(
      state,
      action: PayloadAction<PersonalInformationState>,
    ) {
      state.personalInformationData = action.payload;
    },
    updateApplicationId(state, action: PayloadAction<string>) {
      state.applicationId = action.payload;
    },
    setDigitalApplication(state, action: PayloadAction<boolean>) {
      state.isDigitalApplication = action.payload;
    },
    updateBravoLeadId(state, action: PayloadAction<string>) {
      state.bravoLeadId = action.payload;
    },
    updateGroupId(state, action: PayloadAction<string>) {
      state.groupId = action.payload;
    },
    updateAgentId(state, action: PayloadAction<string>) {
      state.agentId = action.payload;
    },
    removeProfileData(state) {
      state.profileData = initialState.profileData;
    },
    updateCoordinate(state, action: PayloadAction<Position>) {
      state.latLngData = action.payload;
    },
    updateProfileImage(state, action: PayloadAction<ProfileImage>) {
      state.profileData = {
        ...state.profileData,
        selfie_image: action.payload.selfie_image,
        ktp: action.payload.ktp,
      };
    },
    resetAllProfile(state) {
      state.latLngData = initialState.latLngData;
      state.personalInformationData = initialState.personalInformationData;
      state.profileData = initialState.profileData;
    },
    setChangePhoneNumberError(
      state,
      action: PayloadAction<ChangePhoneNumberErrorTypes>,
    ) {
      state.changePhoneNumberError = action.payload;
    },
    resetChangePhoneNumberError(state) {
      state.changePhoneNumberError = initialState.changePhoneNumberError;
    },
    setOTPError(state, action: PayloadAction<OTPErrorTypes>) {
      state.otpError = action.payload;
    },
    resetOTPError(state) {
      state.otpError = initialState.otpError;
    },
    setPhoneNumberNotMatchError(
      state,
      action: PayloadAction<PhoneNumberErrorType>,
    ) {
      state.phoneNumberNotMatchError = action.payload;
    },
    resetPhoneNumberNotMatchError(state) {
      state.phoneNumberNotMatchError = initialState.phoneNumberNotMatchError;
    },
    setTooManyRequestError(
      state,
      action: PayloadAction<TooManyAttemptErrorType>,
    ) {
      state.tooManyAttemptError = action.payload;
    },
    resetTooManyRequestError(state) {
      state.tooManyAttemptError = initialState.tooManyAttemptError;
    },
    setWrongOTPError(state, action: PayloadAction<WrongOTPErrorType>) {
      state.wrongOTPError = action.payload;
    },
    resetWrongOTPError(state) {
      state.wrongOTPError = initialState.wrongOTPError;
    },
    setDomicileOutOfCoverageError(
      state,
      action: PayloadAction<DomicileOutOfCoverageErrorType>,
    ) {
      state.domicileOutOfCoverageError = action.payload;
    },
    resetDomicileOutOfCoverageError(state) {
      state.domicileOutOfCoverageError =
        initialState.domicileOutOfCoverageError;
    },
    setTooManyResendError(
      state,
      action: PayloadAction<TooManyAttemptErrorType>,
    ) {
      state.tooManyAttemptResendError = action.payload;
    },
    resetTooManyResendError(state) {
      state.tooManyAttemptResendError = initialState.tooManyAttemptResendError;
    },
    setOfflineTouchPointAction(
      state,
      action: PayloadAction<TouchPointActionType>,
    ) {
      state.touchPointAction = action.payload;
    },
    setMaximumAttemptEditKTPError(
      state,
      action: PayloadAction<MaximumAttemptEditKTPErrorType>,
    ) {
      state.maximumAttemptEditKTPError = action.payload;
    },
    resetMaximumAttemptEditKTPError(state) {
      state.maximumAttemptEditKTPError =
        initialState.maximumAttemptEditKTPError;
    },
    setMaxAgeApplicantKTPError(
      state,
      action: PayloadAction<MaxAgeApplicantKTPErrorType>,
    ) {
      state.maxAgeApplicantKTPError = action.payload;
    },
    resetMaxAgeApplicantKTPError(state) {
      state.maxAgeApplicantKTPError = initialState.maxAgeApplicantKTPError;
    },
    setSurveyorSubmittedError(
      state,
      action: PayloadAction<SurveyorSubmittedErrorType>,
    ) {
      state.surveyorSubmittedError = action.payload;
    },
    resetSurveyorSubmittedError(state) {
      state.surveyorSubmittedError = initialState.surveyorSubmittedError;
    },
    setUserTypeSubmitted(
      state,
      action: PayloadAction<UserType.CAS | UserType.SURVEYOR>,
    ) {
      state.userTypeSubmitted = action.payload;
    },
    resetUserTypeSubmitted(state) {
      state.userTypeSubmitted = initialState.userTypeSubmitted;
    },
    setGeoplaceError(state, action: PayloadAction<GeoplaceListErrorType>) {
      state.geoplaceError = action.payload;
    },
    resetGeoplaceError(state) {
      state.geoplaceError = initialState.geoplaceError;
    },
    setIsOpenGeoplaceScreen(state) {
      state.isOpenGeoplaceScreen = true;
    },
    resetIsOpenGeoplaceScreen(state) {
      state.isOpenGeoplaceScreen = initialState.isOpenGeoplaceScreen;
    },
    setAgentNotFoundError(
      state,
      action: PayloadAction<AgentNotFoundErrorData>,
    ) {
      state.agentNotFoundError = action.payload;
    },
    resetAgentNotFoundError(state) {
      state.agentNotFoundError = initialState.agentNotFoundError;
    },
    setApplicationNumber(state, action: PayloadAction<string>) {
      state.applicationNumber = action.payload;
    },
    resetApplicationNumber(state) {
      state.applicationNumber = initialState.applicationNumber;
    },
    setLeadExpiredError(state, action: PayloadAction<LeadExpiredErrorData>) {
      state.leadExpiredError = action.payload;
    },
    resetLeadExpiredError(state) {
      state.leadExpiredError = initialState.leadExpiredError;
    },
    setBlackListArea(state) {
      state.isBlackListArea = true;
    },
    resetBlackListArea(state) {
      state.isBlackListArea = initialState.isBlackListArea;
    },
  },
});

export const {
  addPersonalInformationData,
  addProfileData,
  updateProfileData,
  removeProfileData,
  resetAllProfile,
  resetChangePhoneNumberError,
  resetDomicileOutOfCoverageError,
  resetOTPError,
  resetPhoneNumberNotMatchError,
  resetTooManyRequestError,
  resetTooManyResendError,
  resetWrongOTPError,
  setChangePhoneNumberError,
  setDomicileOutOfCoverageError,
  setOTPError,
  setPhoneNumberNotMatchError,
  setTooManyRequestError,
  setTooManyResendError,
  setWrongOTPError,
  updateAddress,
  updateAgentId,
  updateApplicationId,
  setDigitalApplication,
  updateBravoLeadId,
  updateGroupId,
  updateCoordinate,
  updateProfileImage,
  setOfflineTouchPointAction,
  setMaximumAttemptEditKTPError,
  resetMaximumAttemptEditKTPError,
  setMaxAgeApplicantKTPError,
  resetMaxAgeApplicantKTPError,
  setSurveyorSubmittedError,
  resetSurveyorSubmittedError,
  setUserTypeSubmitted,
  resetUserTypeSubmitted,
  setGeoplaceError,
  resetGeoplaceError,
  setIsOpenGeoplaceScreen,
  resetIsOpenGeoplaceScreen,
  setAgentNotFoundError,
  resetAgentNotFoundError,
  setApplicationNumber,
  resetApplicationNumber,
  setLeadExpiredError,
  resetLeadExpiredError,
  setBlackListArea,
  resetBlackListArea,
} = profileSlice.actions;

export default profileSlice.reducer;
